import type { Filter } from "@orsane/schema/filters";
import type { ShortTaskOutput, TaskOutput } from "@orsane/schema/tasks";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { SquareArrowOutUpRight } from "lucide-react";
import { nanoid } from "nanoid";
import { Suspense, useCallback, useMemo } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useSubtaskQueryParams } from "@/hooks/task-query-params/useSubtaskQueryParams";
import { useTRPC } from "@/lib/trpc";
import { allTasksRoute } from "@/routes/AllTasksRoute";
import LoadingSpinner from "../loading-spinner";
import DisplayOptionsPopover from "../task-table/DisplayOptionsPopover";
import { TaskQueryParamsProvider, useTaskQueryParams } from "../task-table/TaskQueryParamsContext";
import TasksTable from "../task-table/TasksTable";
import { Button } from "../ui/button";

export default function SubTasks({ taskId, parentTask }: { taskId: number; parentTask: TaskOutput }) {
    const trpc = useTRPC();
    const queryClient = useQueryClient();
    // Capture the outer (main table) context before we override it with the subtask provider.
    // When a mutation succeeds inside the subtask table, onMutationSuccess invalidates
    // the outer query so the main table picks up the change without a full cache wipe.
    const outerQueryParams = useTaskQueryParams();
    const queryParams = useSubtaskQueryParams(taskId);
    const onMutationSuccess = useCallback(() => {
        queryClient.invalidateQueries(trpc.task.list.queryOptions(outerQueryParams));
    }, [queryClient, trpc, outerQueryParams]);
    const contextValue = useMemo(() => ({ ...queryParams, parentTask, onMutationSuccess }), [queryParams, parentTask, onMutationSuccess]);
    const navigate = useNavigate();

    const handleOpenInFullView = () => {
        const filter = {
            id: nanoid(5),
            type: "parentTask",
            operator: "is",
            value: [
                {
                    id: parentTask.id,
                    key: parentTask.key,
                    number: parentTask.number,
                    title: parentTask.title,
                },
            ] as ShortTaskOutput[],
        } as Filter;
        navigate({ to: allTasksRoute.to, search: () => ({ filter: [filter] }) });
    };

    return (
        <TaskQueryParamsProvider value={contextValue}>
            <div className="absolute top-1.5 right-1.5 z-10">
                <Tooltip>
                    <TooltipTrigger
                        render={
                            <Button variant="ghost" onClick={handleOpenInFullView}>
                                <SquareArrowOutUpRight />
                            </Button>
                        }
                    />
                    <TooltipContent>Open in full view</TooltipContent>
                </Tooltip>
                <DisplayOptionsPopover />
            </div>
            <div className="h-full w-full overflow-hidden flex flex-col relative">
                <Suspense fallback={<LoadingSpinner className="mt-4 ml-3" />}>
                    <TasksTable key="tasklist-all-tasks" disabled={false} />
                </Suspense>
            </div>
        </TaskQueryParamsProvider>
    );
}
